'use client';

import moment from 'moment';
import React, { useCallback } from 'react';

import QuotesActivityLogCard from
  'src/components/job-slide-out/components/activity-event-cards/quotes-event-card';
import InvoiceEventCard from
  'src/components/job-slide-out/components/activity-event-cards/invoice-event-card';
import JobActivityLogCard from
  'src/components/job-slide-out/components/activity-event-cards/job-event-card';
import ReachedOutToTenant from
  'src/components/job-slide-out/components/activity-event-cards/reached-out-to-tenant';
import LumiIntakeEventCard from
  'src/components/job-slide-out/components/activity-event-cards/lumi-intake-event-card';
import { updateUrlForOpenJobSidePanel } from 'src/helpers/job-slide-out';
import { formatCurrency } from 'src/helpers/utils';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import { updateVisibility } from 'src/redux/modals/slice';
import { updateMessage, updateSlideOut } from 'src/redux/jobs/slice';
import type { ActivityLogEntry } from 'src/types';
import { wrapStringWithLink } from 'src/helpers/message-utils';
import type { MediaItem } from 'src/types/quote-approval';
import LumiConversation from '../activity-event-cards/lumi-conversation';
import ResidentCausedEventCard from '../activity-event-cards/resident-caused-event-card';

const cancelRequeueReasonLabels: Record<string, string> = {
  'pro-canceledcant-complete': 'Provider unable to complete the job',
  'pro-no-showedunresponsive': 'Provider unavailable',
};

const statusLogObj: Record<string, string> = {
  closed: 'Maintenance request completed successfully',
  scheduling_in_progress: 'Maintenance request status changed to scheduling in progress',
  scheduled: 'Maintenance request status changed to scheduled',
  quoting: 'Maintenance request status changed to Quoting',
  paused: 'Maintenance request status changed to Paused',
  work_in_progress: 'Maintenance request status changed to Work In Progress',
  work_completed: 'Maintenance request status changed to Work Completed',
  canceled: 'Maintenance request status changed to Canceled',
  quality_check: 'Maintenance request status changed to Quality Check',
  invoice_ready: 'Maintenance request status changed to Invoicing',
};

interface ActivityWrapperProps {
  children: React.ReactNode;
  time: string;
  jobNo?: string;
  jobId?: string | number;
  serviceRequestNo?: string;
  isSystemGenerated?: boolean;
  onClick?: () => void;
  hasPointer?: boolean;
  activityLogInnerClassName?: string;
  id?: string;
}

const ActivityWrapper: React.FC<ActivityWrapperProps> = ({
  children, time, jobNo, jobId,
  serviceRequestNo, isSystemGenerated,
  onClick, hasPointer, activityLogInnerClassName, id
}) => {
  const srId = useAppSelector((state) => state?.jobDetails?.slideOut?.srId);

  const type = useAppSelector((state) => state?.jobDetails?.slideOut?.type);
  return (
    <div className={`activity-log ${isSystemGenerated ? 'user' : ''}`} onClick={onClick} id={id ||""}>

      <div className="activity-log-header">
        {
          time
            ? <div className="activity-log-time">{time}</div>
            : ''
        }
        {type === 'serviceRequestDetails' && jobNo
          ? (
            <div
              className="btn-link"
              onClick={() => updateUrlForOpenJobSidePanel(
                jobNo ?? '',
                'jobDetails',
                jobId ?? '',
                srId ?? '',
              )}
            >
              Job: #
              {jobNo}
            </div>
          ) : ''}
        {type === 'serviceRequestDetails' && serviceRequestNo
          ? (
            <span className="btn-link non-clickable">
              MR: #
              {serviceRequestNo}
            </span>
          ) : ''}
      </div>
      <div className={`activity-log-inner ${hasPointer ? 'cursor-pointer' : ''} ${activityLogInnerClassName || ""}`}>
        {children}
      </div>
    </div>
  );
};

interface ActivityLogCardProps {
  activityLog: ActivityLogEntry;
}

const ActivityLogCard: React.FC<ActivityLogCardProps> = ({ activityLog }) => {
  const dispatch = useAppDispatch();
  const timeZone = useAppSelector((state) => state?.jobDetails?.data?.timezone) || '';

  const eventTime = moment?.utc(
    activityLog?.created_at,
  )?.tz(
    timeZone,
  )?.format('MMM DD, YYYY h:mm A z');
  const eventAttrs = activityLog?.event_attributes;
  const availabilityProvidedByTenant = eventAttrs?.availability_provided_by_tenant;

  const onMessageItemClick = useCallback((
    groupId: unknown,
    activeTab: string,
  ) => {
    dispatch(updateSlideOut({
      activeTab,
    }));
    dispatch(updateMessage({
      activeThreadId: Number(groupId),
    }));
  }, [dispatch]);

  const onImageClickHandler = (
    event: React.MouseEvent,
    collection: MediaItem[],
    index: number,
  ) => {
    event.stopPropagation();
    dispatch(
      updateVisibility({
        modalType: 'imageSlidesModal',
        isVisible: true,
        extraProps: {
          modalData: {
            collection,
            position: index,
            showDeleteButton: false,
          },
        },
      }),
    );
  };

  const logAsRecord = activityLog;

  switch (activityLog?.event || (logAsRecord?.type)) {
    case 'quote.status.changed':
      return (
        <ActivityWrapper
          time={eventTime}
          jobNo={logAsRecord?.job_no}
          jobId={logAsRecord?.job_id}
        >
          <QuotesActivityLogCard logDetails={activityLog} />
        </ActivityWrapper>
      );

    case 'invoice.status.changed':
      return (
        <ActivityWrapper
          time={eventTime}
          jobNo={logAsRecord?.job_no}
          jobId={logAsRecord?.job_id}
        >
          <InvoiceEventCard logDetails={activityLog} />
        </ActivityWrapper>
      );

    case 'maintenance_request.status.changed': {
      const toAttr = eventAttrs?.to;
      const status = typeof toAttr === 'object' && toAttr !== null ? toAttr.value : (toAttr || '');
      return (
        <ActivityWrapper
          time={eventTime}
          jobNo={logAsRecord?.job_no}
          jobId={logAsRecord?.job_id}
          serviceRequestNo={
            logAsRecord
              ?.service_request_no
          }
        >
          <p>
            {status === 'created'
              ? `New maintenance request received from ${
                (eventAttrs?.tenant_name) || ''
              } (tenant)`
              : (statusLogObj?.[status] || '')}
          </p>
        </ActivityWrapper>
      );
    }
    case 'job.status.changed':
      return (
        <ActivityWrapper
          time={eventTime}
          jobNo={logAsRecord?.job_no}
          jobId={
            logAsRecord?.job_id
          }
        >
          <JobActivityLogCard logDetails={activityLog} />
        </ActivityWrapper>
      );
    case 'job.cancelled.requeued': {
      const reasonValue = typeof eventAttrs?.reason === 'object'
        ? eventAttrs?.reason?.value
        : undefined;
      const mappedReason = reasonValue ? cancelRequeueReasonLabels[reasonValue] : undefined;
      if (!mappedReason) return null;
      const serviceLabel = logAsRecord?.service_category?.label || '';
      return (
        <ActivityWrapper
          time={eventTime}
          jobNo={logAsRecord?.job_no}
          jobId={logAsRecord?.job_id}
        >
          <p className="mb-0">
            {`${serviceLabel} job is being reassigned to a new provider`}
          </p>
          <ul>
            <li>
              Reason:
              {' '}
              <span>{mappedReason}</span>
            </li>
            <li>
              New time to follow &mdash; we&apos;re rescheduling now.
            </li>
          </ul>
        </ActivityWrapper>
      );
    }
    case 'job.availability.added': {
      return (
        <ActivityWrapper
          time={eventTime}
          jobNo={logAsRecord?.job_no}
          jobId={
            logAsRecord?.job_id
          }
        >
          <p className="mb-0">
            Availability Provided by tenant:
          </p>
          <ul>
            {Array.isArray(availabilityProvidedByTenant)
              && availabilityProvidedByTenant?.map((avail) => (
                (avail?.availability_date && timeZone) ? (
                  <li key={avail?.availability_date}>
                    {moment.tz(
                      avail?.availability_date,
                      'YYYY-MM-DD',
                      timeZone,
                    ).format('MMM DD, YYYY z')}
                    {' - '}
                    {(avail?.labels)?.join(', ')}
                  </li>
                ) : ''
              ))}
          </ul>
        </ActivityWrapper>
      );
    }
    case 'requesting_availability':
      return (
        <ActivityWrapper
          time={eventTime}
          jobNo={logAsRecord?.job_no}
          jobId={logAsRecord?.job_id}
        >
          <p>
            {eventAttrs?.category}
            {' '}
            job added to
            {' '}
            <span>{eventAttrs?.status}</span>
          </p>
          <p>
            Submitted by
            {' '}
            <span>{eventAttrs?.submitted_by}</span>
          </p>
        </ActivityWrapper>
      );

    case 'job.tenant.reached':
      return (
        <ActivityWrapper
          time={eventTime}
          jobNo={logAsRecord?.job_no}
          jobId={
            logAsRecord?.job_id
          }
        >
          <ReachedOutToTenant
            activityLog={activityLog}
            timeZone={timeZone}
          />
        </ActivityWrapper>
      );
    case 'customer.note.added':
      return (
        <ActivityWrapper
          time={eventTime}
          jobNo={logAsRecord?.job_no}
          jobId={
            logAsRecord?.job_id
          }
        >
          {eventAttrs?.customer_note ? (
            <React.Fragment>
              <p>
                <span>New customer note added</span>
              </p>
              <p>
                <span>
                  Note:
                </span>
                {' '}
                {eventAttrs?.customer_note}
              </p>
            </React.Fragment>
          ) : ''}
        </ActivityWrapper>
      );
    case 'invoice.past.due':
      return (
        <ActivityWrapper
          time={eventTime}
          jobNo={logAsRecord?.job_no}
          jobId={
            logAsRecord?.job_id
          }
        >
          <p className="mb-0">
            <span>{`Invoice #${eventAttrs?.invoice_no} Past Due`}</span>
          </p>
          <p>
            {`Invoice totaling ${
              formatCurrency(Number(eventAttrs?.amount)
                  || 0, 2, 2)
            } is past due.`}
            {(eventAttrs?.freshbooks_share_url) ? (
              <a
                href={eventAttrs?.freshbooks_share_url}
                target="_blank"
                type="button"
                color="link"
                rel="noreferrer"
              >
                View PDF
              </a>
            ) : ''}
          </p>
        </ActivityWrapper>
      );
    case 'job.customer_chat.received':
      return (
        <ActivityWrapper
          time={eventTime}
          jobNo={logAsRecord?.job_no}
          jobId={logAsRecord?.job_id}
          onClick={() => {
            onMessageItemClick(eventAttrs?.customer_chat_group_id, 'message');
          }}
          hasPointer
        >
          <p>
            <span>
              New Message:
            </span>
            {' '}
            {Array.isArray(eventAttrs?.attachments) ? (
              <div className={`preview-image-wrapper ${
                eventAttrs?.attachments?.length === 1 ? 'single-preview' : ''
              } `}
              >
                {eventAttrs?.attachments?.map((image, index) => {
                  const imageURL = image?.thumbnail_url || image?.url;
                  return (
                    <div
                      className="preview-image"
                      key={imageURL}
                      onClick={(event) => {
                        if (eventAttrs?.attachments) {
                          onImageClickHandler(
                            event,
                            eventAttrs?.attachments,
                            index,
                          )
                        }
                      }}
                    >
                      {image?.has_completed
                        ? (
                          <img
                            src={imageURL}
                            alt="preview"
                          />
                        ) : ''}
                      {!image?.has_completed ? (
                        <div className="loader-outer">
                          <div className="loader" />
                        </div>
                      ) : (
                        ''
                      )}
                    </div>
                  );
                })}
              </div>
            ) : ''}
            {wrapStringWithLink({ message: (eventAttrs?.message) || '' })}
          </p>
        </ActivityWrapper>
      );
      
    case 'property.escalation.changed': {
      const escalation = eventAttrs?.property_escalation;
      const isDeEscalation = escalation?.status?.toLowerCase() === 'resolved';
      return (
        <ActivityWrapper
          time={eventTime}
          jobNo={logAsRecord?.job_no}
          jobId={logAsRecord?.job_id}
        >
          <div className="escalation-activity-card">
            <div className="escalation-activity-card__content">
              <p className="escalation-activity-card__title">
                {isDeEscalation ? 'Escalation Flag Removed' : 'Property Flagged as Escalated'}
              </p>
              {escalation?.reason ? (
                <p>{`Reason: ${escalation.reason}`}</p>
              ) : null}
            </div>
            <button
              type="button"
              className="escalation-activity-card__view-btn"
              onClick={() => {
                dispatch(updateVisibility({
                  modalType: 'escalationStatusModal',
                  isVisible: true,
                  extraProps: {
                    escalationId: escalation?.property_escalation_id || '',
                    type: isDeEscalation ? 'de-escalation' : 'escalation',
                    timezone: timeZone,
                  },
                }));
              }}
            >
              View Details
            </button>
          </div>
        </ActivityWrapper>
      );
    }

    case 'flag.changed': {
      const flag = eventAttrs?.flag;
      // Guard against a flag.changed event arriving with no flag payload — without
      // this the card would render a bare "… Added" with no detail.
      if (!flag) return null;
      const isRemoval = flag.change === 'removed';
      // Data-driven label: surface whatever flag_type_label the API sends (the
      // customer-facing flag name), falling back to customer-safe wording when absent.
      const flagLabel = flag.flag_type_label ?? 'Build Back Risk';
      return (
        <ActivityWrapper
          time={eventTime}
          jobNo={logAsRecord?.job_no}
          jobId={logAsRecord?.job_id}
        >
          <div className="flag-activity-card">
            <div className="flag-activity-card__content">
              <p className="flag-activity-card__title">
                {isRemoval
                  ? `${flagLabel} Flag Removed`
                  : `${flagLabel} Flagged`}
              </p>
              {isRemoval ? (
                <>
                  {flag.reason ? (
                    <p>{`Reason: ${flag.reason}`}</p>
                  ) : null}
                  {flag.notes ? (
                    <p>{`Note: ${flag.notes}`}</p>
                  ) : null}
                  {flag.removed_by_name ? (
                    <p>{`Removed by: ${flag.removed_by_name}`}</p>
                  ) : null}
                </>
              ) : (
                // Locked Phase-1 customer-facing detection copy (design QA): a
                // "Reason:" line + System attribution. Attribution is System-only
                // in Phase 1, so the line never names a user.
                <>
                  <p>
                    Reason: System detected that there may be additional work
                    after the repair is complete.
                  </p>
                  <p>Flagged by: System</p>
                </>
              )}
            </div>
          </div>
        </ActivityWrapper>
      );
    }

    case 'lumi.summary.added':
      return (
        <>
        {/* When new UI is implemented for activity log make sure they match it for lumi intake also */}
        <ActivityWrapper
          time={eventTime}
          serviceRequestNo={logAsRecord?.service_request_no}
          activityLogInnerClassName='lumi-intake-card'
          id={activityLog?.activity_uuid}
        >
          <LumiIntakeEventCard logDetails={activityLog} />
        </ActivityWrapper>
          <div className="activity-log">
          <LumiConversation logDetails={activityLog} />
          </div>
        </>
      );
    case 'job.resident_caused.set':
    case 'job.resident_caused.edited':
    case 'job.resident_caused.deleted':
      return (
        <ActivityWrapper
          time={eventTime}
          jobNo={logAsRecord?.job_no}
          jobId={logAsRecord?.job_id}
        >
          <ResidentCausedEventCard logDetails={activityLog} />
        </ActivityWrapper>
      );

    default:
      return null;
  }
};

export default ActivityLogCard;
